import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { produce } from 'immer';

import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';

import { styles } from '@/styles/shared-styles';
import '@/components/editOption';

import { db } from '@/db';
import { debounce } from '@/utils';

import type { MenuOption } from '@/db';

@customElement('app-home')
export class AppHome extends LitElement {
  static styles = [
    styles,
    css`
      #newOption {
        display: flex;
        justify-content: center;
      }
    `,
  ];

  saveData = debounce((data) => {
    db.menuOptions.put(data).then((r) => console.log(r));
  }, 500);

  saveAllData = debounce(() => {
    this.options = produce(this.options, (draft) => {
      for (const [idx, option] of draft.entries()) {
        option.rank = idx;
      }
    });
    db.menuOptions.bulkPut(this.options);
  }, 500);

  @state()
  options: MenuOption[] = [];

  constructor() {
    super();
    db.menuOptions.toArray().then((records) => {
      console.log('Initial records: ', records);
      // This assignment works here, in the constructor, before anything is rendered.
      // Otherwise, we'd need to use something like immer.produce, as in the addOption method
      this.options = records.sort((a, b) => a.rank - b.rank);
    });
  }

  handleNewValues(e: Event) {
    const data = (e as CustomEvent).detail;
    this.saveData(data);
  }

  addOption() {
    const rank = (this?.options?.at(-1)?.rank ?? 0) + 1;
    const optionId = `menuOption-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    this.options = produce(this.options, (draft) => {
      draft.push({
        optionId,
        title: '',
        desc: '',
        price: 0,
        rank,
      });
    });
  }

  moveUp(e: CustomEvent) {
    const idx = this.options.findIndex((o) => o.optionId == e.detail.optionId);
    if (idx != 0) {
      this.options = produce(this.options, (draft) => {
        draft.splice(idx - 1, 0, draft.splice(idx, 1)[0]);
      });
      this.saveAllData();
    }
  }

  moveDown(e: CustomEvent) {
    const idx = this.options.findIndex((o) => o.optionId == e.detail.optionId);
    if (idx != this.options.length - 1) {
      this.options = produce(this.options, (draft) => {
        draft.splice(idx + 1, 0, draft.splice(idx, 1)[0]);
      });
      this.saveAllData();
    }
  }

  trash(e: CustomEvent) {
    const idx = this.options.findIndex((o) => o.optionId == e.detail.optionId);
    if (idx > -1) {
      this.options = produce(this.options, (draft) => {
        draft.splice(idx, 1);
      });
      db.menuOptions.delete(e.detail.optionId);
    }
  }

  render() {
    return html`
      <app-header icon-name="eye-fill" target="show"></app-header>

      <main>
        ${repeat(
          this.options,
          (option) => option.optionId,
          (option) => html`
            <edit-option
              option-id="${option.optionId}"
              title="${option.title}"
              desc="${option.desc}"
              price="${option.price}"
              rank="${option.rank}"
              @value-changed="${this.handleNewValues}"
              @move-up="${this.moveUp}"
              @move-down="${this.moveDown}"
              @trash="${this.trash}"
            ></edit-option>
          `
        )}

        <sl-button
          id="newOption"
          @click="${this.addOption}"
          variant="default"
          size="large"
          circle
        >
          <sl-icon name="plus-lg" label="New Option"></sl-icon>
        </sl-button>
      </main>
    `;
  }
}
